<template>
  <div class="round-stats-content">
    <div v-if="loading" class="round-stats-state">
      {{ tOr('profile.stats.loading', 'Loading statistics...') }}
    </div>

    <div v-else-if="error" class="round-stats-state round-stats-state-error">
      {{ error }}
    </div>

    <div v-else-if="!stats" class="round-stats-state">
      {{ emptyMessage || tOr('judging_program.statistics.empty', 'No statistics available for this round yet.') }}
    </div>

    <template v-else>
      <section class="round-stats-summary">
        <article v-for="card in summaryCards" :key="card.key" class="round-summary-card">
          <span class="round-summary-label">{{ card.label }}</span>
          <strong class="round-summary-value">{{ card.value }}</strong>
        </article>
      </section>

      <section class="round-stats-charts">
        <article v-if="repeatedChartPoints.length" class="round-chart-card round-chart-card-wide">
          <div class="round-chart-header">
            <h4>{{ tOr('judging_program.statistics.repeated_tricks', 'Most repeated tricks') }}</h4>
          </div>
          <div class="round-chart-box round-chart-box-horizontal">
            <canvas ref="repeatedTricksChart"></canvas>
          </div>
        </article>

        <article v-if="trickLevelPoints.length" class="round-chart-card">
          <div class="round-chart-header">
            <h4>{{ tOr('profile.stats.tricks_by_level', 'Tricks by level') }}</h4>
          </div>
          <div class="round-chart-box round-chart-box-doughnut">
            <canvas ref="trickLevelsChart"></canvas>
          </div>
        </article>

        <article v-if="trickCategoryPoints.length" class="round-chart-card">
          <div class="round-chart-header">
            <h4>{{ tOr('profile.stats.trick_categories', 'Trick categories') }}</h4>
          </div>
          <div class="round-chart-box round-chart-box-doughnut">
            <canvas ref="trickCategoriesChart"></canvas>
          </div>
        </article>

        <article v-if="comboScorePoints.length" class="round-chart-card round-chart-card-wide">
          <div class="round-chart-header">
            <h4>{{ tOr('judging_program.statistics.combo_scores', 'Combo scores') }}</h4>
          </div>
          <div class="round-chart-box round-chart-box-line">
            <canvas ref="comboScoresChart"></canvas>
          </div>
        </article>
      </section>

      <section class="round-stats-grid">
        <article class="round-detail-card">
          <div class="round-detail-header">
            <h4>{{ tOr('profile.stats.top_tricks', 'Top tricks') }}</h4>
            <span class="round-count-pill">{{ topTricks.length }}</span>
          </div>
          <div v-if="topTricks.length" class="round-detail-list">
            <div v-for="trick in topTricks" :key="`top-${trick.trick_id}`" class="round-detail-item">
              <div class="round-detail-main">
                <p class="round-detail-name">{{ trick.name }}</p>
                <div class="round-detail-meta">
                  <span v-if="trick.level" class="round-pill">{{ trick.level }}</span>
                  <span v-if="trick.category" class="round-pill round-pill-muted">{{ trick.category }}</span>
                </div>
              </div>
              <div class="round-detail-side">
                <span class="round-score-pill">{{ formatStatValue(trick.best_score) }}</span>
                <small>{{ trick.validated_count }}x</small>
              </div>
            </div>
          </div>
          <p v-else class="round-empty">{{ tOr('profile.stats.top_tricks_empty', 'No validated tricks yet') }}</p>
        </article>

        <article class="round-detail-card">
          <div class="round-detail-header">
            <h4>{{ tOr('judging_program.statistics.unique_by_level', 'Unique tricks by level') }}</h4>
            <span class="round-count-pill">{{ trickLevels.length }}</span>
          </div>
          <div v-if="trickLevels.length" class="round-detail-list">
            <div v-for="level in trickLevels" :key="level.level_id || level.label" class="round-detail-item round-detail-item-stacked">
              <div class="round-detail-main">
                <p class="round-detail-name">{{ level.label }}</p>
                <div class="round-detail-meta">
                  <span>{{ tOr('profile.stats.validated_tricks', 'Validated tricks') }}: {{ level.validated_count }}</span>
                  <span>{{ tOr('profile.stats.unique_tricks', 'Unique tricks') }}: {{ level.unique_count }}</span>
                </div>
              </div>
              <div v-if="level.unique_tricks && level.unique_tricks.length" class="round-inline-list">
                <span v-for="trick in level.unique_tricks" :key="`${level.label}-${trick.trick_id}`" class="round-pill round-pill-outline">
                  {{ trick.name }} · {{ trick.validated_count }}x
                </span>
              </div>
            </div>
          </div>
          <p v-else class="round-empty">{{ tOr('profile.stats.tricks_by_level_empty', 'No level data yet') }}</p>
        </article>
      </section>

      <section class="round-detail-card round-detail-card-full">
        <div class="round-detail-header">
          <h4>{{ tOr('profile.stats.combos', 'Combos') }}</h4>
          <span class="round-count-pill">{{ combos.length }}</span>
        </div>
        <div v-if="combos.length" class="round-combo-list">
          <article v-for="combo in combos" :key="combo.combo_number" class="round-combo-card">
            <button type="button" class="round-combo-toggle" @click="toggleCombo(combo.combo_number)">
              <div>
                <p class="round-detail-name">{{ tOr('judging_program.statistics.combo_label', 'Combo') }} {{ combo.combo_number }}</p>
                <div class="round-detail-meta">
                  <span>{{ combo.tricks_count }} {{ tOr('profile.stats.tricks', 'tricks') }}</span>
                  <span v-if="combo.levels && combo.levels.length">{{ combo.levels.join(' / ') }}</span>
                </div>
              </div>
              <div class="round-detail-side round-detail-side-inline">
                <span class="round-score-pill">{{ formatStatValue(combo.total_score) }}</span>
                <span class="round-chevron">{{ isComboExpanded(combo.combo_number) ? '−' : '+' }}</span>
              </div>
            </button>
            <div v-if="isComboExpanded(combo.combo_number)" class="round-combo-body">
              <div class="round-inline-list round-inline-list-spaced">
                <span v-for="trick in combo.tricks" :key="`${combo.combo_number}-${trick.validated_trick_id || trick.trick_id}`" class="round-pill round-pill-outline">
                  {{ trick.name }} · {{ formatStatValue(trick.score) }}
                </span>
              </div>
            </div>
          </article>
        </div>
        <p v-else class="round-empty">{{ tOr('profile.stats.combos_empty', 'No combos recorded yet') }}</p>
      </section>

      <section class="round-detail-card round-detail-card-full">
        <div class="round-detail-header">
          <h4>{{ tOr('profile.stats.penalties', 'Penalties') }}</h4>
          <span class="round-count-pill">{{ penaltyTotal }}</span>
        </div>
        <div v-if="penaltyTypes.length" class="round-detail-list">
          <div v-for="penalty in penaltyTypes" :key="penalty.dub_id || penalty.label" class="round-detail-item round-detail-item-stacked">
            <div class="round-detail-main">
              <p class="round-detail-name">{{ penalty.label }}</p>
              <div class="round-detail-meta">
                <span>{{ penalty.count }} {{ tOr('profile.stats.affected_tricks', 'affected tricks') }}</span>
              </div>
            </div>
            <div v-if="penalty.affected_tricks && penalty.affected_tricks.length" class="round-inline-list">
              <span v-for="trick in penalty.affected_tricks" :key="`${penalty.label}-${trick.trick_id}`" class="round-pill round-pill-warning">
                {{ trick.name }} · {{ trick.validated_count }}x
              </span>
            </div>
          </div>
        </div>
        <p v-else class="round-empty">{{ tOr('profile.stats.penalties_empty', 'No penalties recorded yet') }}</p>
      </section>
    </template>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'RoundStatisticsContent',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
    emptyMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      repeatedTricksChartInstance: null,
      trickLevelsChartInstance: null,
      trickCategoriesChartInstance: null,
      comboScoresChartInstance: null,
      expandedCombos: {},
      chartRenderToken: 0,
    };
  },
  computed: {
    summaryCards() {
      const summary = this.stats?.summary || {};
      return [
        { key: 'score', label: this.tOr('judging_program.totals_tricks_box.total_scores', 'Total score'), value: this.formatStatValue(summary.total_score) },
        { key: 'variety', label: this.tOr('judging_program.totals_tricks_box.total_variety', 'Variety'), value: this.formatInteger(summary.variety_score) },
        { key: 'style', label: this.tOr('judging_program.totals_tricks_box.total_style', 'Style'), value: this.formatPercentInteger(summary.style_score) },
        { key: 'validated', label: this.tOr('profile.stats.validated_tricks', 'Validated tricks'), value: this.formatInteger(summary.validated_tricks_count) },
        { key: 'unique', label: this.tOr('profile.stats.unique_tricks', 'Unique tricks'), value: this.formatInteger(summary.unique_tricks_count) },
        { key: 'combos', label: this.tOr('profile.stats.combos', 'Combos'), value: this.formatInteger(summary.combos_count) },
        { key: 'longest', label: this.tOr('judging_program.statistics.longest_combo', 'Longest combo'), value: this.formatInteger(summary.longest_combo_tricks_count) },
        { key: 'penalties', label: this.tOr('profile.stats.penalties', 'Penalties'), value: this.formatInteger(summary.penalties_count) },
      ];
    },
    topTricks() {
      return Array.isArray(this.stats?.tricks?.top_tricks) ? this.stats.tricks.top_tricks : [];
    },
    trickLevels() {
      return Array.isArray(this.stats?.tricks?.by_level) ? this.stats.tricks.by_level : [];
    },
    combos() {
      return Array.isArray(this.stats?.combos?.items) ? this.stats.combos.items : [];
    },
    penaltyTypes() {
      return Array.isArray(this.stats?.penalties?.by_type) ? this.stats.penalties.by_type : [];
    },
    penaltyTotal() {
      return this.formatInteger(this.stats?.penalties?.total || 0);
    },
    repeatedChartPoints() {
      return Array.isArray(this.stats?.charts?.repeated_tricks) ? this.stats.charts.repeated_tricks : [];
    },
    trickLevelPoints() {
      return Array.isArray(this.stats?.charts?.tricks_by_level) ? this.stats.charts.tricks_by_level : [];
    },
    trickCategoryPoints() {
      return Array.isArray(this.stats?.charts?.tricks_by_category) ? this.stats.charts.tricks_by_category : [];
    },
    comboScorePoints() {
      return Array.isArray(this.stats?.charts?.combo_scores) ? this.stats.charts.combo_scores : [];
    },
    shouldRenderCharts() {
      return Boolean(this.stats) && !this.loading && !this.error;
    },
  },
  watch: {
    stats() {
      this.expandedCombos = {};
      this.scheduleRenderCharts();
    },
    loading() {
      this.scheduleRenderCharts();
    },
    error() {
      this.scheduleRenderCharts();
    },
  },
  mounted() {
    this.scheduleRenderCharts();
  },
  beforeUnmount() {
    this.chartRenderToken += 1;
    this.destroyCharts();
  },
  methods: {
    tOr(key, fallback) {
      if (typeof this.$te === 'function' && this.$te(key)) {
        return this.$t(key);
      }

      return fallback;
    },
    formatStatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return value;
      }

      return numericValue.toFixed(2);
    },
    formatInteger(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return '0';
      }

      return String(Math.round(numericValue));
    },
    formatPercentInteger(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return '0%';
      }

      return `${Math.round(numericValue)}%`;
    },
    toggleCombo(comboNumber) {
      this.expandedCombos = {
        ...this.expandedCombos,
        [comboNumber]: !this.expandedCombos[comboNumber],
      };
    },
    isComboExpanded(comboNumber) {
      return Boolean(this.expandedCombos[comboNumber]);
    },
    scheduleRenderCharts() {
      const token = ++this.chartRenderToken;

      this.$nextTick(() => {
        if (token !== this.chartRenderToken) {
          return;
        }

        this.renderCharts();
      });
    },
    destroyCharts() {
      [
        'repeatedTricksChartInstance',
        'trickLevelsChartInstance',
        'trickCategoriesChartInstance',
        'comboScoresChartInstance',
      ].forEach((key) => {
        if (this[key]) {
          this[key].stop();
          this[key].destroy();
          this[key] = null;
        }
      });
    },
    renderCharts() {
      this.destroyCharts();

      if (!this.shouldRenderCharts) {
        return;
      }

      this.renderRepeatedTricksChart();
      this.renderTrickLevelsChart();
      this.renderTrickCategoriesChart();
      this.renderComboScoresChart();
    },
    renderRepeatedTricksChart() {
      const canvas = this.$refs.repeatedTricksChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.repeatedChartPoints.length) {
        return;
      }

      this.repeatedTricksChartInstance = new Chart(context, {
        type: 'bar',
        data: {
          labels: this.repeatedChartPoints.map((item) => item.label),
          datasets: [{
            data: this.repeatedChartPoints.map((item) => Number(item.value) || 0),
            backgroundColor: '#2563eb',
            borderRadius: 8,
          }],
        },
        options: {
          animation: false,
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { beginAtZero: true, ticks: { precision: 0 } },
            y: { ticks: { autoSkip: false } },
          },
        },
      });
    },
    renderTrickLevelsChart() {
      const canvas = this.$refs.trickLevelsChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.trickLevelPoints.length) {
        return;
      }

      this.trickLevelsChartInstance = new Chart(context, {
        type: 'doughnut',
        data: {
          labels: this.trickLevelPoints.map((item) => item.label),
          datasets: [{
            data: this.trickLevelPoints.map((item) => Number(item.validated_count) || Number(item.value) || 0),
            backgroundColor: ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0f766e'],
            borderWidth: 0,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          cutout: '58%',
        },
      });
    },
    renderTrickCategoriesChart() {
      const canvas = this.$refs.trickCategoriesChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.trickCategoryPoints.length) {
        return;
      }

      this.trickCategoriesChartInstance = new Chart(context, {
        type: 'doughnut',
        data: {
          labels: this.trickCategoryPoints.map((item) => item.label),
          datasets: [{
            data: this.trickCategoryPoints.map((item) => Number(item.validated_count) || Number(item.value) || 0),
            backgroundColor: ['#0f766e', '#0891b2', '#7c2d12', '#9333ea', '#be123c', '#4f46e5', '#65a30d'],
            borderWidth: 0,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          cutout: '58%',
        },
      });
    },
    renderComboScoresChart() {
      const canvas = this.$refs.comboScoresChart;
      const context = canvas?.isConnected ? canvas.getContext('2d') : null;
      if (!context || !this.comboScorePoints.length) {
        return;
      }

      this.comboScoresChartInstance = new Chart(context, {
        type: 'line',
        data: {
          labels: this.comboScorePoints.map((item) => item.label),
          datasets: [{
            data: this.comboScorePoints.map((item) => Number(item.value) || 0),
            borderColor: '#111827',
            backgroundColor: 'rgba(17, 24, 39, 0.14)',
            tension: 0.25,
            fill: true,
          }],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.round-stats-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.round-stats-state {
  padding: 1rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #475569;
}

.round-stats-state-error {
  border-style: solid;
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.round-stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

.round-summary-card,
.round-detail-card,
.round-chart-card,
.round-combo-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
}

.round-summary-card {
  padding: 0.95rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.round-summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.round-summary-value {
  font-size: 1.35rem;
  color: #111827;
}

.round-stats-charts,
.round-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.round-chart-card-wide,
.round-detail-card-full {
  grid-column: 1 / -1;
}

.round-chart-card {
  padding: 1rem;
  min-width: 0;
}

.round-chart-header,
.round-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.round-chart-header h4,
.round-detail-header h4 {
  margin: 0;
  font-size: 1rem;
}

.round-chart-box {
  min-height: 260px;
  position: relative;
  width: 100%;
}

.round-chart-box canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
}

.round-detail-card {
  padding: 1rem;
  min-width: 0;
}

.round-detail-list,
.round-combo-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.round-detail-item,
.round-combo-toggle {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 0.9rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  min-width: 0;
}

.round-detail-item-stacked {
  flex-direction: column;
  align-items: flex-start;
}

.round-detail-main {
  flex: 1;
  min-width: 0;
}

.round-detail-name {
  margin: 0;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.round-detail-meta,
.round-inline-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.35rem;
  color: #4b5563;
  font-size: 0.86rem;
}

.round-inline-list-spaced {
  margin-top: 0.75rem;
}

.round-detail-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

.round-detail-side-inline {
  flex-direction: row;
  align-items: center;
}

.round-pill,
.round-count-pill,
.round-score-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.18rem 0.5rem;
  font-size: 0.76rem;
  font-weight: 600;
}

.round-pill {
  background: #e0e7ff;
  color: #3730a3;
}

.round-pill-muted,
.round-pill-outline {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.round-pill-warning {
  background: #fff7ed;
  color: #c2410c;
}

.round-count-pill {
  background: #e5e7eb;
  color: #111827;
}

.round-score-pill {
  background: #111827;
  color: #ffffff;
}

.round-combo-card {
  overflow: hidden;
}

.round-combo-toggle {
  width: 100%;
  border: 0;
  text-align: left;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.round-combo-body {
  padding: 0 0.9rem 0.9rem;
}

.round-chevron {
  font-size: 1.2rem;
  line-height: 1;
}

.round-empty {
  margin: 0;
  color: #94a3b8;
  font-style: italic;
}

@media (max-width: 768px) {
  .round-stats-summary,
  .round-stats-charts,
  .round-stats-grid {
    grid-template-columns: 1fr;
  }

  .round-chart-box {
    min-height: 220px;
    height: clamp(220px, 58vw, 300px);
  }

  .round-chart-box-horizontal {
    min-height: 320px;
    height: clamp(320px, 88vw, 460px);
  }

  .round-chart-box-doughnut {
    min-height: 240px;
    height: clamp(240px, 70vw, 320px);
  }

  .round-chart-box-line {
    min-height: 240px;
    height: clamp(240px, 62vw, 320px);
  }

  .round-detail-item,
  .round-combo-toggle {
    flex-direction: column;
    align-items: flex-start;
  }

  .round-detail-side {
    width: 100%;
    align-items: flex-start;
  }

  .round-detail-side-inline {
    justify-content: space-between;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .round-chart-card,
  .round-detail-card {
    padding: 0.85rem;
  }

  .round-chart-box-horizontal {
    min-height: 360px;
    height: clamp(360px, 108vw, 520px);
  }
}
</style>